import { CheckCircle, Dot } from '@/components/pages/icons';
import Alert from '@/components/pages/alert';
import clsx from 'clsx';
import { Lifebuoy } from './icons';
import type { FormCompletionQualified } from '@jlg-website/shared';

type QualifiedMessageProps = {
  data: FormCompletionQualified;
  className?: string;
};

/**
 * Completion message shown to a qualified lead (Figma node 6089-17121). Content is
 * admin-managed via the `form_messages` entity: a receipt confirmation, the "what's next"
 * timeline (each step's icon resolved from the icon library) and a help callout.
 */
export default function QualifiedMessage({ data, className }: QualifiedMessageProps) {
  const steps = data.steps ?? [];
  const totalSteps = steps.length;

  return (
    <div className={clsx('text-brand-text-button', className)}>
      {data.alert && (
        <Alert variant="success" icon={<CheckCircle />}>
          <div dangerouslySetInnerHTML={{ __html: data.alert }} />
        </Alert>
      )}

      {data.title && (
        <h2
          className="pt-8 text-[32px] font-normal leading-[120%] tracking-[-0.96px] @lg:text-5xl @lg:leading-[110%] @lg:tracking-[-1.44px]"
          dangerouslySetInnerHTML={{ __html: data.title }}
        />
      )}

      {totalSteps > 0 && (
        <ul className="flex flex-col gap-6 mb-8 pt-2">
          {steps.map((step, index) => {
            const active = index === 0;

            return (
              <li className="flex items-start gap-2 relative" key={`step-${index}`}>
                <div
                  className={clsx(
                    'shrink-0 flex items-center justify-center w-10 h-10 rounded-full z-10 box-content [&_svg]:size-5',
                    active ? 'bg-brand-accent text-white' : 'bg-[#E5E7EB]'
                  )}
                  dangerouslySetInnerHTML={{ __html: step.icon }}
                />
                <div className="flex flex-col items-start gap-1">
                  <div
                    className="text-base leading-normal"
                    dangerouslySetInnerHTML={{ __html: step.title }}
                  />
                  <div className="flex items-center gap-2">
                    {active && <Dot className="w-3 animate-pulse text-brand-accent" />}
                    <p
                      className={clsx(
                        'text-sm leading-5 tracking-[0.21px]',
                        active ? 'text-brand-accent font-bold' : 'text-[#6B7280] font-medium'
                      )}
                    >
                      {step.description}
                    </p>
                  </div>
                  {index < totalSteps - 1 && (
                    <div className="absolute left-4.5 top-11 w-0.5 h-[calc(100%-24px)] bg-black/20">
                      {active && <div className="bg-brand-accent h-1/2 w-full" />}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {data.help && (
        <Alert icon={<Lifebuoy />}>
          <div dangerouslySetInnerHTML={{ __html: data.help }} />
        </Alert>
      )}
    </div>
  );
}
